import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { Grid } from "@mui/material";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Input } from "../../../../../../../../../../components/Form";
import MapInput from "../../../../../../../../../../components/Form/MapInput";
import Modal from "../../../../../../../../../../components/Generic/Modal";
import { Location } from "../../../../../../../../../../models/location";
import { ValueOf } from "../../../../../../../../../../types";
import { AddressTypeKeys } from "../../../../../../../../../../util/enums";
import { useDespatchContext } from "../../../../../../context/DespatchContext";
import useStyles from "./styles";

interface ModalProps {
  hasError?: boolean;
  type: "delivery" | "pickUp";
}

export interface AddressModalHandles {
  open: () => void;
  close: () => void;
}

const AddressModal: ForwardRefRenderFunction<AddressModalHandles, ModalProps> = (props, ref) => {
  const { hasError, type } = props;

  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { handleAddAddress, handleUpdateAddress, addressToUpdate, stopEditingAddress } =
    useDespatchContext();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [location, setLocation] = useState<Location>({});
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const open = useCallback(() => {
    setOpenModal(true);
    if (!addressToUpdate) {
      setLocation({});
      setErrors([]);
    }
  }, []);
  const close = useCallback(() => {
    setOpenModal(false);
  }, []);

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  const handleUpdateLocation = (locationKey: keyof Location, value: ValueOf<Location>) => {
    setLocation((prev) => {
      return { ...prev, [locationKey]: value };
    });
  };

  useEffect(() => {
    if (addressToUpdate) {
      setLocation(addressToUpdate.address);
      setErrors([]);
    }
  }, [addressToUpdate]);

  const schema = yup.object().shape({
    longAddress: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
  });

  const clearFields = () => {
    setLocation({});
  };

  const isValid = () => {
    try {
      schema.validateSync(location, { abortEarly: false });
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error?.inner);
      }

      return false;
    }
  };

  const handleClose = () => {
    stopEditingAddress();
    clearFields();
    close();
  };

  const handleSubmit = () => {
    const key =
      type === "delivery" ? AddressTypeKeys.DESTINATION_ADDRESS : AddressTypeKeys.ORIGIN_ADDRESS;

    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    if (isValid() && location) {
      if (addressToUpdate) {
        handleUpdateAddress(key, location, addressToUpdate?.index);
        handleClose();
      } else {
        handleAddAddress(key, location);
        handleClose();
      }
    }
  };

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      close();
    }
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) isValid();
  }, [
    hasFormBeenSubmitted,
    ...Object.keys(location).map((key) => location[key as keyof Location]),
  ]);

  const generateBaseFieldProps = (name: keyof Location) => {
    const fieldProps = {
      error: !!getErrorByField(name),
      errorMessage: getErrorByField(name),
      "data-cy": `input-${name}`,
      dataCy: name,
      label: translate(name),
      placeholder: translate(name),
      "aria-label": name,
      name,
      value: location[name],
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        handleUpdateLocation(name, event.target.value);
      },
    };

    return fieldProps;
  };

  const modalTitle = useMemo(
    () =>
      translate(
        `${addressToUpdate ? "edit" : "new"}${type.charAt(0).toUpperCase() + type.slice(1)}Address`
      ),
    [type]
  );

  return (
    <Modal
      open={openModal}
      handleClose={handleClose}
      title={modalTitle}
      disableEnforceFocus
      data-cy={modalTitle}
      dataCyTitle={modalTitle}
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: () => handleClose(),
        dataCySave: "submitChangesButton",
        textSave: addressToUpdate ? translate("update") : translate("create"),
        loading: false,
        onSave: () => handleSubmit(),
      }}
    >
      <section className={classes.modalSection}>
        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <Grid item xs={12} lg={12}>
            <Typography
              fontFamily="Inter"
              fontWeight={600}
              fontSize="16px"
              lineHeight="24px"
              color="#000"
            >
              {translate("details")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input {...generateBaseFieldProps("name")} />
          </Grid>
          <Grid item xs={12}>
            <MapInput
              location={location}
              onChange={(value) => {
                setLocation((prev) => {
                  return { ...prev, ...(value as Location), addressLine1: value.longAddress };
                });
              }}
              label={translate("address1")}
              onChangeInput={(value) => {
                handleUpdateLocation("longAddress", value);
                handleUpdateLocation("addressLine1", value);
              }}
              inputValue={location?.longAddress}
              placeholder="Google Maps search"
              data-cy={`input-addressLine1`}
              error={!!getErrorByField("longAddress")}
              errorMessage={getErrorByField("longAddress")}
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              {...generateBaseFieldProps("addressLine2")}
              label={translate("address2")}
              placeholder={translate("address2")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input {...generateBaseFieldProps("suburb")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input {...generateBaseFieldProps("state")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input
              {...generateBaseFieldProps("postalcode")}
              label={translate("postalCode")}
              placeholder={translate("postalCode")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input {...generateBaseFieldProps("country")} />
          </Grid>
        </Grid>
      </section>
    </Modal>
  );
};

export default forwardRef(AddressModal);
